// src/stores/thesisStore.ts
// Zustand store for Thesis Workspace

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  ThesisStore,
  Thesis,
  ThesisCondition,
  ThesisHealthTrend,
  HealthScoreBreakdown,
} from "@/types/thesis";

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

const generateId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const now = (): string => new Date().toISOString();

// ─────────────────────────────────────────────────────────────────────────────
// Health Score Calculation
// ─────────────────────────────────────────────────────────────────────────────

function calculateHealthScoreFromConditions(
  conditions: ThesisCondition[]
): HealthScoreBreakdown {
  if (conditions.length === 0) {
    return {
      score: 0,
      trend: "stable",
      conditionsMet: 0,
      conditionsTotal: 0,
      weightedScore: 0,
      factors: [],
    };
  }

  let totalWeight = 0;
  let weightedMet = 0;
  const factors: HealthScoreBreakdown["factors"] = [];

  for (const condition of conditions) {
    const weight = condition.weight ?? 1;
    totalWeight += weight;

    if (condition.isMet) {
      weightedMet += weight;
    }

    factors.push({
      conditionId: condition.id,
      label: condition.label ?? condition.prompt ?? "Condition",
      isMet: condition.isMet,
      weight,
      contribution: condition.isMet ? (weight / totalWeight) * 100 : 0,
    });
  }

  const score = totalWeight > 0 ? Math.round((weightedMet / totalWeight) * 100) : 0;
  const conditionsMet = conditions.filter((c) => c.isMet).length;

  // Update contributions now that we have total weight
  for (const factor of factors) {
    factor.contribution = factor.isMet
      ? Math.round((factor.weight / totalWeight) * 100)
      : 0;
  }

  return {
    score,
    trend: "stable", // Trend is calculated separately based on history
    conditionsMet,
    conditionsTotal: conditions.length,
    weightedScore: weightedMet,
    factors,
  };
}

function determineTrend(
  currentScore: number,
  previousScore: number | undefined
): ThesisHealthTrend {
  if (previousScore === undefined) return "stable";
  const diff = currentScore - previousScore;
  if (diff > 5) return "improving";
  if (diff < -5) return "deteriorating";
  return "stable";
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useThesisStore = create<ThesisStore>()(
  persist(
    (set, get) => ({
      // Initial state
      theses: {},
      activeThesisId: null,

      // ─────────────────────────────────────────────────────────────────────
      // Thesis CRUD
      // ─────────────────────────────────────────────────────────────────────

      createThesis: (dashboardId, title, description) => {
        const id = generateId("thesis");
        const timestamp = now();

        const thesis: Thesis = {
          id,
          dashboardId,
          title,
          description,
          conditions: [],
          conditionLogic: "all",
          status: "draft",
          healthScore: 0,
          healthTrend: "stable",
          createdAt: timestamp,
          updatedAt: timestamp,
        };

        set((state) => ({
          theses: {
            ...state.theses,
            [id]: thesis,
          },
          activeThesisId: id,
        }));

        return id;
      },

      updateThesis: (id, updates) => {
        set((state) => {
          const thesis = state.theses[id];
          if (!thesis) return state;

          return {
            theses: {
              ...state.theses,
              [id]: {
                ...thesis,
                ...updates,
                updatedAt: now(),
              },
            },
          };
        });
      },

      deleteThesis: (id) => {
        set((state) => {
          const { [id]: _deleted, ...remaining } = state.theses;
          void _deleted; // Acknowledge intentionally unused variable
          return {
            theses: remaining,
            activeThesisId:
              state.activeThesisId === id ? null : state.activeThesisId,
          };
        });
      },

      setActiveThesis: (id) => {
        set({ activeThesisId: id });
      },

      getThesis: (id) => {
        return get().theses[id];
      },

      getThesisByDashboard: (dashboardId) => {
        const theses = Object.values(get().theses);
        return theses.find((t) => t.dashboardId === dashboardId);
      },

      // ─────────────────────────────────────────────────────────────────────
      // Condition CRUD
      // ─────────────────────────────────────────────────────────────────────

      addCondition: (thesisId, conditionData) => {
        const conditionId = generateId("cond");
        const timestamp = now();

        const condition: ThesisCondition = {
          ...conditionData,
          id: conditionId,
          isMet: conditionData.isMet ?? false,
          createdAt: timestamp,
          updatedAt: timestamp,
        };

        set((state) => {
          const thesis = state.theses[thesisId];
          if (!thesis) return state;

          const updatedThesis: Thesis = {
            ...thesis,
            conditions: [...thesis.conditions, condition],
            updatedAt: timestamp,
          };

          return {
            theses: {
              ...state.theses,
              [thesisId]: updatedThesis,
            },
          };
        });

        // Recalculate health score
        get().updateHealthScore(thesisId);

        return conditionId;
      },

      updateCondition: (thesisId, conditionId, updates) => {
        const timestamp = now();

        set((state) => {
          const thesis = state.theses[thesisId];
          if (!thesis) return state;

          const updatedConditions = thesis.conditions.map((c) =>
            c.id === conditionId
              ? { ...c, ...updates, updatedAt: timestamp }
              : c
          );

          return {
            theses: {
              ...state.theses,
              [thesisId]: {
                ...thesis,
                conditions: updatedConditions,
                updatedAt: timestamp,
              },
            },
          };
        });

        // Recalculate health score if condition met status changed
        if ("isMet" in updates) {
          get().updateHealthScore(thesisId);
        }
      },

      removeCondition: (thesisId, conditionId) => {
        const timestamp = now();

        set((state) => {
          const thesis = state.theses[thesisId];
          if (!thesis) return state;

          return {
            theses: {
              ...state.theses,
              [thesisId]: {
                ...thesis,
                conditions: thesis.conditions.filter((c) => c.id !== conditionId),
                updatedAt: timestamp,
              },
            },
          };
        });

        // Recalculate health score
        get().updateHealthScore(thesisId);
      },

      // ─────────────────────────────────────────────────────────────────────
      // Health Score
      // ─────────────────────────────────────────────────────────────────────

      updateHealthScore: (thesisId) => {
        const thesis = get().theses[thesisId];
        if (!thesis) return;

        const previousScore = thesis.healthScore;
        const breakdown = calculateHealthScoreFromConditions(thesis.conditions);
        const trend = determineTrend(breakdown.score, previousScore);

        get().updateThesis(thesisId, {
          healthScore: breakdown.score,
          healthTrend: trend,
        });
      },

      // ─────────────────────────────────────────────────────────────────────
      // Status Management
      // ─────────────────────────────────────────────────────────────────────

      setStatus: (thesisId, status) => {
        get().updateThesis(thesisId, { status });
      },
    }),
    {
      name: "abi-theses",
      partialize: (state) => ({
        theses: state.theses,
        activeThesisId: state.activeThesisId,
      }),
    }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────────

export const useActiveThesis = () => {
  const theses = useThesisStore((s) => s.theses);
  const activeThesisId = useThesisStore((s) => s.activeThesisId);
  return activeThesisId ? theses[activeThesisId] : null;
};

/**
 * Get a thesis by its ID
 */
export const useThesisById = (id: string | null | undefined) => {
  const theses = useThesisStore((s) => s.theses);
  return id ? theses[id] : null;
};

export const useThesisByDashboard = (dashboardId: string | null | undefined) => {
  const theses = useThesisStore((s) => s.theses);
  if (!dashboardId) return null;
  return Object.values(theses).find((t) => t.dashboardId === dashboardId) ?? null;
};

export const useThesisConditions = (thesisId: string | null | undefined) => {
  const thesis = useThesisById(thesisId);
  return thesis?.conditions ?? [];
};

export const useThesisHealth = (thesisId: string | null | undefined) => {
  const thesis = useThesisById(thesisId);
  if (!thesis) return null;
  return {
    score: thesis.healthScore ?? 0,
    trend: thesis.healthTrend ?? "stable",
    status: thesis.status,
  };
};

